
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useIsMobile } from '@/hooks/use-is-mobile';

export type TutorialStep = {
  id: number;
  title: string;
  description: string;
  target?: string; // CSS selector for the target element
  position?: 'top' | 'right' | 'bottom' | 'left';
  image?: string; // Optional image path
};

const tutorialSteps: TutorialStep[] = [
  {
    id: 1,
    title: "Welkom bij de Plattegrond Editor!",
    description: "Deze korte tutorial laat je zien hoe je een plattegrond kunt maken. Je kunt op elk moment stoppen door op 'Overslaan' te klikken.",
  },
  {
    id: 2,
    title: "Objecten toevoegen",
    description: "Gebruik de zijbalk om objecten te klikken en sleep ze naar de plattegrond om ze toe te voegen.",
    target: "#floor-plan-container",
    position: "right",
    image: "/lovable-uploads/722acbd0-26df-49d7-8396-609f166e3978.png"
  },
  {
    id: 3,
    title: "Objecten bewerken",
    description: "Klik op een object om het te selecteren. Je kunt het verplaatsen, draaien of verwijderen.",
    target: "#floor-plan-container",
    position: "bottom",
    image: "/lovable-uploads/f9b50578-5433-425e-8121-9cc05da6bdd6.png"
  },
  {
    id: 4,
    title: "Plattegrond opslaan",
    description: "Gebruik de 'Opslaan' knop om je werk op te slaan of 'Opslaan als' om een nieuwe kopie te maken. Je kunt ook in- en uitzoomen, of objecten vergrendelen voor de perfecte layout. Als je niet alle toestellen kunt zien, klik dan op 'Passend' om de volledige plattegrond te tonen.",
    target: "button[aria-label='Opslaan']",
    position: "bottom"
  },
  {
    id: 5,
    title: "Openen - Mijn Plattegronden",
    description: "Via de 'Openen' knop vind je al je opgeslagen plattegronden. Hier kun je ze bewerken, kopiëren, namen wijzigen en afbeeldingen uploaden. Je kunt ook screenshots maken van je plattegrond en deze direct plakken als voorvertoning.",
    position: "bottom",
    image: "/lovable-uploads/3da75062-6b38-40c1-8172-0fc9e24b52a4.png"
  },
  {
    id: 6,
    title: "Klaar!",
    description: "Je bent nu klaar om je eigen plattegronden te maken. Veel succes! Heb je vragen of loop je ergens tegenaan? Mail ons gerust op info@beterturnen.nl en we helpen je verder.",
  },
];

type TutorialContextType = {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  steps: TutorialStep[];
  startTutorial: () => void;
  endTutorial: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  shouldNeverShowAgain: boolean;
  setShouldNeverShowAgain: (value: boolean) => void;
  currentStepData: TutorialStep | undefined;
};

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const TutorialProvider = ({ children }: { children: ReactNode }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [shouldNeverShowAgain, setShouldNeverShowAgain] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    const hasCompletedTutorial = localStorage.getItem('hasCompletedTutorial');
    
    if (!hasSeenTutorial && !isMobile && !hasCompletedTutorial) {
      const timer = setTimeout(() => {
        setIsActive(true);
        localStorage.setItem('hasSeenTutorial', 'true');
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  const startTutorial = () => {
    setCurrentStep(1);
    setIsActive(true);
    localStorage.setItem('hasSeenTutorial', 'true');
  };

  const endTutorial = () => {
    setIsActive(false);
    if (shouldNeverShowAgain) {
      localStorage.setItem('hasCompletedTutorial', 'true');
    }
  };

  const nextStep = () => {
    if (currentStep < tutorialSteps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      endTutorial();
      localStorage.setItem('hasCompletedTutorial', 'true');
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= tutorialSteps.length) {
      setCurrentStep(step);
    }
  };

  const currentStepData = tutorialSteps.find(step => step.id === currentStep);

  return (
    <TutorialContext.Provider
      value={{
        isActive,
        currentStep,
        totalSteps: tutorialSteps.length,
        steps: tutorialSteps,
        startTutorial,
        endTutorial,
        nextStep,
        prevStep,
        goToStep,
        shouldNeverShowAgain,
        setShouldNeverShowAgain,
        currentStepData,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};
